import { useGameContext } from '@3d-components/GameContextProvider';
import { WebGLNotSupported } from '@3d-components/WebGLErrors';
import { useGetInitialSettings } from '@3d-components/hooks';
import { initBabylon } from '@3d-lib/babylonjs/init';
import { setHumanoid } from '@3d-lib/babylonjs/setHumanoid';
import { updateCamera } from '@3d-lib/babylonjs/utils';
import { defaultState, mainStateReducer } from '@3d-lib/mainStateUtils';
import { MainState, MainStateAction, MainStateDispatch, Model } from '@3d-lib/types';
import React, { lazy, useEffect, useRef } from 'react';
import { useImmerReducer } from 'use-immer';
import styled from 'styled-components';
import { SettingsModal } from './Settings';
import { Settings } from 'lucide-react';

const Chat = lazy(() => import('@3d-components/chat/Chat.client').then((module) => ({ default: module.Chat })));
const CanvasContainer = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
    border-radius: 1rem;
    background: #f5f5f5;
`;

const SettingsButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:hover {
        background: rgba(255, 255, 255, 1);
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
        transform: scale(0.95);
    }
`;

export interface CanvasThatDoesNotReRenderProps {
    mainStateDispatch: MainStateDispatch;
    modelName: Model | null;
}

const CanvasThatDoesNotReRender = React.memo(function CanvasThatDoesNotReRender({
    mainStateDispatch,
    modelName,
}: CanvasThatDoesNotReRenderProps) {
    const { humanoidRef } = useGameContext();
    const didInitRef = useRef(false);
    const sceneRef = useRef<any>(null);

    useEffect(() => {
        // This use effect is called when the model changes. If there is an existing model, it
        // first removes the existing model from the scene, then adds the new model to the scene.
        if (humanoidRef.current && modelName) {
            try {
                const scene = humanoidRef.current.scene;
                sceneRef.current = scene;

                // Properly dispose of the current humanoid
                humanoidRef.current.dispose();
                humanoidRef.current = null;

                // Set up the new humanoid
                setHumanoid(humanoidRef, modelName, scene);
                updateCamera(modelName, scene);
            } catch (error) {
                console.error('Error during model change:', error);
                mainStateDispatch({
                    type: 'SET_INIT_ERROR_MESSAGE',
                    payload: `Model change failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                });
            }
        }
    }, [modelName, humanoidRef, mainStateDispatch]);

    useEffect(() => {
        if (!modelName) {
            // If modelName is not set, then we probably haven't loaded the settings from the URL yet.
            return;
        }

        if (didInitRef.current) {
            // If we're here, then strict mode is on - reactStrictMode is true on the next.config.js
            console.warn('Warning - tried to initialize twice. Will skip 2nd initialization.');
            return;
        }

        try {
            const setIsLoading = (isLoading: boolean) => {
                mainStateDispatch({ type: 'SET_IS_LOADING', payload: isLoading });
            };
            const scene = initBabylon(setIsLoading, modelName);
            console.log('scene', scene);
            sceneRef.current = scene;

            setHumanoid(humanoidRef, modelName, scene);
        } catch (error) {
            console.error('Error during initialization:', error);
            if (error instanceof Error) {
                mainStateDispatch({ type: 'SET_INIT_ERROR_MESSAGE', payload: error.message });
            }
        }

        didInitRef.current = true;
    }, [mainStateDispatch, humanoidRef, modelName]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            // Cleanup on unmount
            if (humanoidRef.current) {
                try {
                    humanoidRef.current.dispose();
                    humanoidRef.current = null;
                } catch (error) {
                    console.error('Error during cleanup:', error);
                }
            }
        };
    }, [humanoidRef]);

    return (
        <canvas
            id="renderCanvas"
            style={{
                position: 'absolute',
                height: '100%',
                width: '100%',
                left: 0,
                top: 0,
            }}
        />
    );
});

type GameProps = {
    hasGoogleApiKey: boolean;
    defaultInitialMessage?: string;
    hiddenMessageInput?: boolean;
    onChangedMainState?: (mainState: MainState) => void;
};

const Game: React.FC<GameProps> = ({
    hasGoogleApiKey,
    defaultInitialMessage,
    hiddenMessageInput,
    onChangedMainState,
}) => {
    const [mainState, mainStateDispatch] = useImmerReducer<MainState, MainStateAction>(
        mainStateReducer,
        (() => {
            const stored = window.localStorage.getItem('avatarState');
            const parsed = stored ? JSON.parse(stored) : {};
            return {
                ...defaultState,
                ...parsed,
                settings: {
                    ...defaultState.settings,
                    ...(parsed.settings ?? {}),
                    initialMessage: defaultInitialMessage || defaultState.settings.initialMessage,
                },
            };
        })(),
    );

    useGetInitialSettings(mainStateDispatch, mainState.settings);

    useEffect(() => {
        if (onChangedMainState) {
            onChangedMainState(mainState);
        }
    }, [mainState]);

    if (mainState.initErrorMessage.includes('WebGL not supported')) {
        console.log('❌ WebGL not supported, showing WebGLNotSupported'); // Debug log
        return <WebGLNotSupported />;
    }

    return (
        <CanvasContainer>
            <Chat
                mainStateDispatch={mainStateDispatch}
                chatState={mainState.chatState}
                settings={mainState.settings}
                hideMessages={true}
                hiddenMessageInput={hiddenMessageInput}
            />

            <CanvasThatDoesNotReRender mainStateDispatch={mainStateDispatch} modelName={mainState.settings.model} />

            <SettingsButton onClick={() => mainStateDispatch({ type: 'TOGGLE_SETTINGS_MODAL' })}>
                <Settings size={24} />
            </SettingsButton>

            <SettingsModal
                mainStateDispatch={mainStateDispatch}
                settings={mainState.settings}
                hasGoogleApiKey={hasGoogleApiKey}
                isOpen={mainState.settingsModalIsOpen}
            />
        </CanvasContainer>
    );
};

export default Game;
